import { Component, JSX, For, Show, createSignal, createMemo, createEffect } from "solid-js";

export type SortDirection = "asc" | "desc" | null;

export interface Column<T> {
  id: string;
  header: string | JSX.Element;
  accessor: (row: T) => JSX.Element | string | number | null;
  sortable?: boolean;
  sortFn?: (a: T, b: T) => number;
  width?: string;
  align?: "left" | "center" | "right";
  class?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (row: T) => string;
  loading?: boolean;
  emptyMessage?: string | JSX.Element;
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  onRowClick?: (row: T) => void;
  selectedKeys?: Set<string>;
  onSelectionChange?: (keys: Set<string>) => void;
  selectable?: boolean;
  class?: string;
}

export function DataTable<T>(props: DataTableProps<T>): JSX.Element {
  const [sortColumn, setSortColumn] = createSignal<string | null>(null);
  const [sortDirection, setSortDirection] = createSignal<SortDirection>(null);

  const handleSort = (column: Column<T>) => {
    if (!column.sortable) return;

    if (sortColumn() === column.id) {
      if (sortDirection() === "asc") {
        setSortDirection("desc");
      } else if (sortDirection() === "desc") {
        setSortDirection(null);
        setSortColumn(null);
      } else {
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column.id);
      setSortDirection("asc");
    }
  };

  const sortedData = createMemo(() => {
    const col = sortColumn();
    const dir = sortDirection();
    if (!col || !dir) return props.data;

    const column = props.columns.find((c) => c.id === col);
    if (!column) return props.data;

    const sorted = [...props.data].sort((a, b) => {
      if (column.sortFn) {
        return dir === "asc" ? column.sortFn(a, b) : -column.sortFn(a, b);
      }

      const aVal = column.accessor(a);
      const bVal = column.accessor(b);

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return dir === "asc" ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal);
      const bStr = String(bVal);
      return dir === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    return sorted;
  });

  const handleSelectAll = (checked: boolean) => {
    if (!props.onSelectionChange) return;
    if (checked) {
      const allKeys = new Set(props.data.map((row) => props.keyExtractor(row)));
      props.onSelectionChange(allKeys);
    } else {
      props.onSelectionChange(new Set());
    }
  };

  const handleSelectRow = (key: string, checked: boolean) => {
    if (!props.onSelectionChange || !props.selectedKeys) return;
    const newSelection = new Set(props.selectedKeys);
    if (checked) {
      newSelection.add(key);
    } else {
      newSelection.delete(key);
    }
    props.onSelectionChange(newSelection);
  };

  const isAllSelected = createMemo(() => {
    if (!props.selectedKeys || props.data.length === 0) return false;
    return props.data.every((row) => props.selectedKeys!.has(props.keyExtractor(row)));
  });

  const isSomeSelected = createMemo(() => {
    if (!props.selectedKeys || props.data.length === 0) return false;
    const count = props.data.filter((row) => props.selectedKeys!.has(props.keyExtractor(row)))
      .length;
    return count > 0 && count < props.data.length;
  });

  const tableClasses = createMemo(() => {
    const classes = ["table", "w-full"];
    if (props.striped !== false) classes.push("table-zebra");
    if (props.compact) classes.push("table-sm");
    return classes.join(" ");
  });

  const alignClasses: Record<string, string> = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };

  return (
    <div class={`overflow-x-auto ${props.class ?? ""}`}>
      <table class={tableClasses()}>
        <thead>
          <tr>
            <Show when={props.selectable}>
              <th class="w-12">
                <input
                  type="checkbox"
                  class="checkbox checkbox-sm"
                  checked={isAllSelected()}
                  ref={(el) => {
                    createEffect(() => {
                      el.indeterminate = isSomeSelected();
                    });
                  }}
                  onChange={(e) => handleSelectAll(e.currentTarget.checked)}
                />
              </th>
            </Show>
            <For each={props.columns}>
              {(column) => (
                <th
                  class={`${column.sortable ? "cursor-pointer select-none hover:bg-base-200" : ""} ${
                    alignClasses[column.align ?? "left"]
                  } ${column.class ?? ""}`}
                  style={{ width: column.width }}
                  aria-sort={column.sortable ? (sortColumn() === column.id ? (sortDirection() === "asc" ? "ascending" : sortDirection() === "desc" ? "descending" : "none") : "none") : undefined}
                  onClick={() => handleSort(column)}>
                  <div class="flex items-center gap-1">
                    <span>{column.header}</span>
                    <Show when={column.sortable}>
                      <span class="text-xs opacity-50">
                        {sortColumn() === column.id
                          ? sortDirection() === "asc"
                            ? "\u25B2"
                            : sortDirection() === "desc"
                              ? "\u25BC"
                              : "\u21C5"
                          : "\u21C5"}
                      </span>
                    </Show>
                  </div>
                </th>
              )}
            </For>
          </tr>
        </thead>
        <tbody>
          <Show when={props.loading}>
            <tr>
              <td colspan={props.columns.length + (props.selectable ? 1 : 0)} class="text-center py-8" role="status" aria-busy="true">
                <span class="loading loading-spinner loading-lg" aria-hidden="true"></span>
              </td>
            </tr>
          </Show>
          <Show when={!props.loading && sortedData().length === 0}>
            <tr>
              <td
                colspan={props.columns.length + (props.selectable ? 1 : 0)}
                class="text-center py-8 text-base-content/50"
                role="status">
                {props.emptyMessage ?? "No data available"}
              </td>
            </tr>
          </Show>
          <Show when={!props.loading && sortedData().length > 0}>
            <For each={sortedData()}>
              {(row) => {
                const key = props.keyExtractor(row);
                const isSelected = props.selectedKeys?.has(key) ?? false;

                return (
                  <tr
                    class={`${props.hoverable !== false ? "hover" : ""} ${
                      props.onRowClick ? "cursor-pointer" : ""
                    } ${isSelected ? "bg-primary/10" : ""}`}
                    onClick={() => props.onRowClick?.(row)}>
                    <Show when={props.selectable}>
                      <td class="w-12" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          class="checkbox checkbox-sm"
                          checked={isSelected}
                          onChange={(e) => handleSelectRow(key, e.currentTarget.checked)}
                        />
                      </td>
                    </Show>
                    <For each={props.columns}>
                      {(column) => (
                        <td
                          class={`${alignClasses[column.align ?? "left"]} ${column.class ?? ""}`}
                          style={{ width: column.width }}>
                          {column.accessor(row)}
                        </td>
                      )}
                    </For>
                  </tr>
                );
              }}
            </For>
          </Show>
        </tbody>
      </table>
    </div>
  );
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  pageSize?: number;
  showInfo?: boolean;
  class?: string;
}

export const Pagination: Component<PaginationProps> = (props) => {
  const showInfo = () => props.showInfo !== false;
  const startItem = () => (props.currentPage - 1) * (props.pageSize ?? 10) + 1;
  const endItem = () => Math.min(props.currentPage * (props.pageSize ?? 10), props.totalItems ?? 0);

  const pageNumbers = createMemo(() => {
    const pages: (number | "...")[] = [];
    const total = props.totalPages;
    const current = props.currentPage;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push("...");

      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);

      for (let i = start; i <= end; i++) pages.push(i);

      if (current < total - 2) pages.push("...");
      pages.push(total);
    }

    return pages;
  });

  return (
    <div class={`flex items-center justify-between gap-4 ${props.class ?? ""}`}>
      <Show when={showInfo() && props.totalItems !== undefined}>
        <div class="text-sm text-base-content/60">
          Showing {startItem()} to {endItem()} of {props.totalItems} results
        </div>
      </Show>
      <div class="join">
        <button
          class="join-item btn btn-sm"
          disabled={props.currentPage <= 1}
          onClick={() => props.onPageChange(props.currentPage - 1)}>
          \u00AB
        </button>
        <For each={pageNumbers()}>
          {(page) => (
            <Show
              when={page !== "..."}
              fallback={<button class="join-item btn btn-sm btn-disabled">...</button>}>
              <button
                class={`join-item btn btn-sm ${page === props.currentPage ? "btn-active" : ""}`}
                onClick={() => props.onPageChange(page as number)}>
                {page}
              </button>
            </Show>
          )}
        </For>
        <button
          class="join-item btn btn-sm"
          disabled={props.currentPage >= props.totalPages}
          onClick={() => props.onPageChange(props.currentPage + 1)}>
          \u00BB
        </button>
      </div>
    </div>
  );
};

interface PaginatedTableProps<T> extends Omit<DataTableProps<T>, "data"> {
  data: T[];
  pageSize?: number;
  defaultPage?: number;
}

export function PaginatedTable<T>(props: PaginatedTableProps<T>): JSX.Element {
  const pageSize = () => props.pageSize ?? 10;
  const [currentPage, setCurrentPage] = createSignal(props.defaultPage ?? 1);

  const totalPages = createMemo(() => Math.ceil(props.data.length / pageSize()));

  const paginatedData = createMemo(() => {
    const start = (currentPage() - 1) * pageSize();
    return props.data.slice(start, start + pageSize());
  });

  // Reset to page 1 when data changes
  createMemo(() => {
    if (currentPage() > totalPages() && totalPages() > 0) {
      setCurrentPage(1);
    }
  });

  return (
    <div class="space-y-4">
      <DataTable {...props} data={paginatedData()} />
      <Show when={totalPages() > 1}>
        <Pagination
          currentPage={currentPage()}
          totalPages={totalPages()}
          onPageChange={setCurrentPage}
          totalItems={props.data.length}
          pageSize={pageSize()}
        />
      </Show>
    </div>
  );
}
